// src/admin/screens/admins/modals/ChangeRoleModal.tsx
// Super-admin changes another admin's role. Writes to admin_users.role
// directly (RLS policy admin_users_super_admin_write allows it) and
// records an audit row with the before/after roles + reason.
//
// A "reason" is required and audit-logged — role changes are an
// account-integrity event. The reason is a freeform 10+ char string;
// not validated server-side, just captured.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { AdminRow, AdminRole } from '@admin/hooks/useAdmins';
import styles from './AdminModals.module.css';

const ROLES = ['super_admin', 'admin', 'collections'] as const satisfies readonly AdminRole[];

interface Props {
  admin: AdminRow;
  onClose: (refresh: boolean) => void;
}

export default function ChangeRoleModal({ admin, onClose }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [role, setRole] = useState<AdminRole>(admin.role);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (role === admin.role) {
      onClose(false);
      return;
    }
    if (reason.trim().length < 10) {
      toast({ kind: 'error', msg: 'Reason must be at least 10 characters.' });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ role })
        .eq('id', admin.id);
      if (error) throw error;
      await recordAudit({
        action: 'admin.change_role',
        target_type: 'admin',
        target_id: admin.id,
        payload: { from: admin.role, to: role, reason: reason.trim() },
      });
      toast({ kind: 'success', msg: 'Role changed.' });
      await qc.invalidateQueries({ queryKey: ['admin', admin.id] });
      await qc.invalidateQueries({ queryKey: ['admins'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Change role — ${admin.display_name}`}
    >
      <form onSubmit={submit} className={styles.form}>
        <FormField label="New role" htmlFor="cr-role" required>
          <select
            id="cr-role"
            value={role}
            onChange={(e) => setRole(e.target.value as AdminRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          label="Reason"
          htmlFor="cr-reason"
          hint="Required and audit-logged. Describe why this role is changing."
          required
        >
          <textarea
            id="cr-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Change role
          </Button>
        </div>
      </form>
    </Modal>
  );
}
